import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AlertTriangle, Download, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type DgiFiscalIdentity,
  type FiscalIdentity,
  type FiscalIdentityUpdate,
} from "@/services/fiscalIdentityService";

/**
 * Edit the emisor block that lands on every CFE: sucursal, domicilio fiscal,
 * ciudad, departamento.
 *
 * "Traer de DGI" shows what DGI holds BESIDE what we have, per field, rather
 * than overwriting. The two known-bad cases point in opposite directions —
 * on sucursal DGI is authoritative and we go stale (ALTEZ DE BRUM was moved
 * from 2 to 4 and every invoice was rejected), while on domicilio DGI
 * sometimes answers with nothing and the stored value is the better one. So
 * neither source can be declared the winner in advance; the human picks.
 *
 * Presentational on purpose: the tenant's settings page and the accountant's
 * client-detail page pass their own loaders and saver, so one component
 * serves both surfaces without knowing which tenant it is editing.
 */

export interface SettingsFiscalIdentityCardProps {
  /** Identifies the query cache entry — the tenant id, or "current". */
  scopeKey: string;
  load: () => Promise<FiscalIdentity>;
  loadFromDgi: () => Promise<DgiFiscalIdentity>;
  save: (updates: FiscalIdentityUpdate) => Promise<FiscalIdentity>;
  /** Hidden for roles that may read but not write. */
  canEdit?: boolean;
  /**
   * "card" (default) renders its own Card, which is how the accountant's
   * client-detail page uses it. "plain" drops the Card and header title so it
   * can sit inside a dialog — the tenant's own Settings merged this into the
   * Datos legales card, where a second card-in-a-card would be chrome for its
   * own sake.
   *
   * The sucursal/domicilio warning renders in BOTH: those two fields are
   * stamped on every CFE and a wrong sucursal gets comprobantes rejected, so
   * it is not header decoration to be dropped with the header.
   */
  variant?: "card" | "plain";
}

type FormState = {
  cdgDGISucur: string;
  address: string;
  city: string;
  department: string;
  phone: string;
  contactEmail: string;
};

function toFormState(identity: FiscalIdentity): FormState {
  return {
    cdgDGISucur: String(identity.cdgDGISucur ?? 1),
    address: identity.address ?? "",
    city: identity.city ?? "",
    department: identity.department ?? "",
    phone: identity.phone ?? "",
    contactEmail: identity.contactEmail ?? "",
  };
}

export function SettingsFiscalIdentityCard({
  scopeKey,
  load,
  loadFromDgi,
  save,
  canEdit = true,
  variant = "card",
}: SettingsFiscalIdentityCardProps): JSX.Element {
  const queryClient = useQueryClient();
  const queryKey = ["fiscal-identity", scopeKey];
  const [form, setForm] = useState<FormState | null>(null);
  const [dgi, setDgi] = useState<DgiFiscalIdentity | null>(null);

  const { data: identity, isLoading } = useQuery({
    queryKey,
    queryFn: load,
    refetchOnWindowFocus: false,
  });

  // Seed the form once the identity lands, and re-seed after a save so the
  // inputs show what the server actually stored (trimmed, nulled, clamped).
  useEffect(() => {
    if (identity) setForm(toFormState(identity));
  }, [identity]);

  const dgiMutation = useMutation({
    mutationFn: loadFromDgi,
    onSuccess: (data) => {
      setDgi(data);
      if (!data.available) {
        toast.error("No pudimos consultar DGI en este momento.");
      }
    },
    onError: () => toast.error("No pudimos consultar DGI en este momento."),
  });

  const saveMutation = useMutation({
    mutationFn: save,
    onSuccess: async (saved) => {
      setForm(toFormState(saved));
      await queryClient.invalidateQueries({ queryKey });
      toast.success("Datos fiscales actualizados.");
    },
    onError: (err: unknown) => {
      const message =
        typeof err === "object" && err !== null && "response" in err
          ? ((err as { response?: { data?: { message?: string } } }).response
              ?.data?.message ?? null)
          : null;
      toast.error(message ?? "No pudimos guardar los datos fiscales.");
    },
  });

  const dirty = useMemo(() => {
    if (!identity || !form) return false;
    const original = toFormState(identity);
    return (
      original.cdgDGISucur !== form.cdgDGISucur ||
      original.address !== form.address ||
      original.city !== form.city ||
      original.department !== form.department ||
      original.phone !== form.phone ||
      original.contactEmail !== form.contactEmail
    );
  }, [identity, form]);

  if (isLoading || !form) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Datos fiscales del emisor</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Cargando…
          </div>
        </CardContent>
      </Card>
    );
  }

  const fields: Array<{
    key: keyof FormState;
    label: string;
    hint?: string;
    dgiValue: string | null;
    inputMode?: "numeric";
  }> = [
    {
      key: "cdgDGISucur",
      label: "Código de sucursal",
      hint: "Si no coincide con DGI, todos tus comprobantes se rechazan.",
      dgiValue: dgi?.cdgDGISucur != null ? String(dgi.cdgDGISucur) : null,
      inputMode: "numeric",
    },
    {
      key: "address",
      label: "Domicilio fiscal",
      hint: "Máximo 70 caracteres — DGI trunca ahí.",
      dgiValue: dgi?.address ?? null,
    },
    { key: "city", label: "Ciudad", dgiValue: dgi?.city ?? null },
    {
      key: "department",
      label: "Departamento",
      dgiValue: dgi?.department ?? null,
    },
    {
      key: "phone",
      label: "Teléfono de contacto",
      dgiValue: dgi?.phone ?? null,
    },
    {
      key: "contactEmail",
      label: "Correo de contacto",
      dgiValue: dgi?.contactEmail ?? null,
    },
  ];

  const handleSave = (): void => {
    const sucursal = Number.parseInt(form.cdgDGISucur, 10);
    if (!Number.isInteger(sucursal) || sucursal < 1) {
      toast.error("El código de sucursal debe ser un número entero mayor o igual a 1.");
      return;
    }
    saveMutation.mutate({
      cdgDGISucur: sucursal,
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      department: form.department.trim() || null,
      phone: form.phone.trim() || null,
      contactEmail: form.contactEmail.trim() || null,
    });
  };

  const dgiButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={dgiMutation.isPending}
      onClick={() => dgiMutation.mutate()}
      className="inline-flex shrink-0 items-center gap-2"
    >
      {dgiMutation.isPending ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        <Download className="h-4 w-4 shrink-0" aria-hidden />
      )}
      Traer de DGI
    </Button>
  );

  /**
   * Printed in BOTH variants. Sucursal and domicilio land on every CFE and a
   * sucursal that disagrees with DGI gets every comprobante rejected, so this
   * is a warning, not a card subtitle to be dropped along with the header.
   */
  const printedOnEveryCfeNote = (
    <p className="text-sm text-muted-foreground">
      Sucursal y domicilio van impresos en cada comprobante que emitís; el
      resto son tus datos de contacto.
    </p>
  );

  const body = (
    <>
        <dl className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
          <div>
            <dt className="inline">RUT: </dt>
            <dd className="inline font-mono text-foreground">{identity?.rut}</dd>
          </div>
          <div>
            <dt className="inline">Razón social: </dt>
            <dd className="inline text-foreground">
              {identity?.legalName ?? "—"}
            </dd>
          </div>
        </dl>

        {fields.map((field) => {
          const current = form[field.key];
          const differs =
            field.dgiValue != null && field.dgiValue !== current;
          const isSucursal = field.key === "cdgDGISucur";
          return (
            <div key={field.key} className="space-y-1.5">
              <Label htmlFor={`fiscal-${field.key}`}>{field.label}</Label>
              <Input
                id={`fiscal-${field.key}`}
                value={current}
                inputMode={field.inputMode}
                disabled={!canEdit || saveMutation.isPending}
                onChange={(e) =>
                  setForm({ ...form, [field.key]: e.target.value })
                }
              />
              {field.hint ? (
                <p className="text-xs text-muted-foreground">{field.hint}</p>
              ) : null}
              {differs ? (
                <div
                  className={`flex flex-wrap items-center gap-2 rounded-[var(--fx-r-md)] px-3 py-2 text-xs ${
                    isSucursal
                      ? "bg-[rgba(201,94,62,0.08)]"
                      : "bg-[var(--fx-s-tile)]"
                  }`}
                >
                  {isSucursal ? (
                    <AlertTriangle
                      className="h-3.5 w-3.5 shrink-0 text-[var(--fx-warn-text)]"
                      aria-hidden
                    />
                  ) : null}
                  <span>
                    DGI dice{" "}
                    <strong className="font-mono">
                      {field.dgiValue === "" ? "(vacío)" : field.dgiValue}
                    </strong>
                  </span>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setForm({ ...form, [field.key]: field.dgiValue ?? "" })
                      }
                    >
                      Usar este
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}

        {dgi && !dgi.available ? (
          <p className="text-xs text-[var(--fx-warn-text)]">
            DGI no respondió. Los datos de arriba siguen siendo editables a mano.
          </p>
        ) : null}

        {canEdit ? (
          <div className="flex justify-end">
            <Button
              type="button"
              disabled={!dirty || saveMutation.isPending}
              onClick={handleSave}
            >
              {saveMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : null}
              Guardar
            </Button>
          </div>
        ) : null}
    </>
  );

  if (variant === "plain") {
    return (
      <div className="space-y-4">
        <div className="flex flex-row flex-wrap items-start justify-between gap-2">
          {printedOnEveryCfeNote}
          {dgiButton}
        </div>
        {body}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
        <div className="space-y-1.5">
          <CardTitle>Datos fiscales del emisor</CardTitle>
          <CardDescription>
            Sucursal y domicilio van impresos en cada comprobante que emitís;
            el resto son tus datos de contacto.
          </CardDescription>
        </div>
        {dgiButton}
      </CardHeader>
      <CardContent className="space-y-4">{body}</CardContent>
    </Card>
  );
}
